import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

// Sem `chat`: cria conversa. Com `chat` (grupo): edita nome e participantes
const InternalChatModal = ({ open, onClose, chat, onSaved }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(chat ? chat.users.filter((u) => u.id !== user.id) : []);
    setTitle(chat?.title || "");
  }, [open, chat, user.id]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/users", {
          params: { searchParam: search, pageNumber: 1 },
        });
        setOptions(data.users.filter((u) => u.id !== user.id));
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [open, search, user.id]);

  const needsTitle = !!chat || selected.length > 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      userIds: chat
        ? [user.id, ...selected.map((u) => u.id)]
        : selected.map((u) => u.id),
      title: needsTitle ? title : undefined,
    };
    try {
      const { data } = chat
        ? await api.put(`/chats/${chat.id}`, payload)
        : await api.post("/chats", payload);
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {i18n.t(chat ? "internalChat.modal.titleEdit" : "internalChat.modal.titleNew")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Autocomplete
            multiple
            className={classes.field}
            options={options}
            value={selected}
            loading={loading}
            filterSelectedOptions
            getOptionLabel={(option) => option.name || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            onChange={(e, value) => setSelected(value)}
            onInputChange={(e, value, reason) => {
              if (reason === "input") setSearch(value);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  size="small"
                  label={option.name}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("internalChat.modal.users")}
                variant="outlined"
                autoFocus
              />
            )}
          />
          {needsTitle && (
            <TextField
              label={i18n.t("internalChat.modal.groupTitle")}
              helperText={i18n.t("internalChat.modal.groupHelp")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
              fullWidth
              required
              inputProps={{ maxLength: 80 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("internalChat.modal.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={saving || selected.length === 0}
            className={classes.btnWrapper}
          >
            {i18n.t(chat ? "internalChat.modal.save" : "internalChat.modal.create")}
            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InternalChatModal;
